// Offline write queue. A quiz submit that fails because the device has no
// connection is kept in IndexedDB and replayed in order once it is back. Each
// item is tagged with the account that made it, so one family member's saved
// answers never replay under another account. Nothing is dropped silently: an
// item the server rejects stays in the queue, marked failed, until someone
// retries it.
//
// This file has no imports from the API client, so the client can import it
// without a cycle. The sender is passed in by the caller.

const DB_NAME = "hv-offline";
const STORE = "writes";
const CHANGED = "hv:queue-changed";

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run(mode, fn) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = fn(tx.objectStore(STORE));
    tx.oncomplete = () => { db.close(); resolve(request.result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error); };
  });
}

function notify() {
  window.dispatchEvent(new Event(CHANGED));
}

// The signed-in account, read from the bearer token's `sub` claim. Only the
// id is read. The token itself is never stored in the queue.
export function currentUserId() {
  try {
    const token = localStorage.getItem("hv_token");
    if (!token) return null;
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part)).sub || null;
  } catch {
    return null;
  }
}

export async function enqueueWrite({ method, url, data }) {
  const item = {
    uid: currentUserId(),
    method,
    url,
    data: data ?? null,
    status: "pending",
    error: null,
    createdAt: Date.now(),
  };
  await run("readwrite", (store) => store.add(item));
  notify();
}

export function listWrites() {
  return run("readonly", (store) => store.getAll()).then((rows) => rows || []);
}

async function update(id, patch) {
  const row = await run("readonly", (store) => store.get(id));
  if (!row) return;
  await run("readwrite", (store) => store.put({ ...row, ...patch }));
}

async function remove(id) {
  await run("readwrite", (store) => store.delete(id));
}

// Replays this account's pending writes, oldest first. Stops at the first
// failure so that later writes never run ahead of an earlier one.
//   - No response at all: still offline. Stop, and wait for the next "online".
//   - The server rejected it: mark it failed, keep it, and stop.
export async function flushQueue(send) {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  const uid = currentUserId();
  const pending = (await listWrites())
    .filter((item) => item.uid === uid && item.status === "pending")
    .sort((a, b) => a.id - b.id);

  for (const item of pending) {
    try {
      await send(item);
      await remove(item.id);
    } catch (err) {
      if (!err?.response) break;
      const detail = err.response.data?.detail;
      await update(item.id, {
        status: "failed",
        error: typeof detail === "string" ? detail : `Server returned ${err.response.status}`,
      });
      break;
    }
    notify();
  }
  notify();
}

// Puts a failed item back in line for another attempt.
export async function retryFailed() {
  const uid = currentUserId();
  const failed = (await listWrites()).filter((item) => item.uid === uid && item.status === "failed");
  for (const item of failed) await update(item.id, { status: "pending", error: null });
  notify();
}

export function subscribeQueue(fn) {
  window.addEventListener(CHANGED, fn);
  return () => window.removeEventListener(CHANGED, fn);
}
